import type { FeatureMiddleware } from '@cast/api-client';
import {
  demoClusterSecurityInsightsBestPractice,
  demoClusterSecurityInsightsBestPractices,
  demoClusterSecurityInsightsBestPracticesFilters,
  demoClusterSecurityInsightsImages,
  demoClusterSecurityInsightsImageDigests,
  demoClusterSecurityInsightsImageDetails,
  demoClusterSecurityInsightsImageVulnerabilities,
  demoClusterSecurityInsightsImageVulnerabilityDetails,
  demoClusterSecurityInsightsImagePackages,
  demoClusterSecurityInsightsImageResources,
  demoOrganizationSecurityInsightsAgents,
  demoOrganizationSecurityInsightsAttackPaths,
  demoOrganizationSecurityInsightsAttackPathDetails,
  demoOrganizationSecurityInsightsResourceDetails,
  demoClusterSecurityInsightsBestPracticeResources,
  demoOrganizationSecurityInsightsBestPracticeOverview,
  demoOrganizationSecurityInsightsImageSecurityOverview,
  demoOrganizationSecurityInsightsAttackPathsOverview,
  demoOrganizationSecurityInsightsClustersSettings,
  demoOrganizationSecurityInsightsWorkloads,
  demoOrganizationSecurityInsightsVulnerabilitiesTrend,
} from '@cast/fixtures';
import { isDemoCluster, isSimulatedDemoClusterId } from '@cast/utils';

import {
  getIsClusterDemoMode,
  getIsSecurityDemoMode,
  setHasEnabledSecurityAgents,
} from '@cast/infrastructure-demo';

import { rejectDemoClusterRequest } from '../utils';

export const securityInsights: FeatureMiddleware<'securityInsights'> = {
  getBestPracticeFilters: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsBestPracticesFilters();
      return resolve(fixture);
    }
    return next();
  },
  getContainerImagesFilters: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsBestPracticesFilters();
      return resolve(fixture);
    }
    return next();
  },
  getBestPracticeChecks: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsBestPractices();
      return resolve(fixture);
    }
    return next();
  },
  getBestPracticeCheck: async ({ args, next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsBestPractice(
        args[0]?.ruleId
      );
      if (fixture) {
        return resolve(fixture);
      }
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getBestPracticeCheckResources: async ({ args, next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsBestPracticeResources(
        args[0]?.ruleId
      );
      if (fixture) {
        return resolve(fixture);
      }
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getAgentsStatuses: async ({ next, resolve }) => {
    const response = await next();

    // Check if security agents are enabled (excluding demo cluster)
    const agentStatuses = response.data.agentStatuses;
    const hasEnabledAgents =
      agentStatuses &&
      Object.entries(agentStatuses).some(
        ([clusterId, status]) => !isDemoCluster(clusterId) && status === true
      );

    // Set raw fact - security demo mode is derived automatically
    setHasEnabledSecurityAgents(!!hasEnabledAgents);

    // Return fixture in cluster demo mode
    if (getIsClusterDemoMode()) {
      const fixture = await demoOrganizationSecurityInsightsAgents();
      return resolve(fixture);
    }

    return response;
  },
  getContainerImages: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsImages();
      return resolve(fixture);
    }
    return next();
  },
  getImageDetails: async ({ args, next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsImageDetails(
        args[0]?.tagId
      );
      if (fixture) {
        return resolve(fixture);
      }
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getImageDigests: async ({ args, next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsImageDigests(
        args[0]?.tagId
      );
      if (fixture) {
        return resolve(fixture);
      }
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getImageVulnerabilities: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsImageVulnerabilities();
      return resolve(fixture);
    }
    return next();
  },
  getImagePackageVulnerabilityDetails: async ({ args, next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture =
        await demoClusterSecurityInsightsImageVulnerabilityDetails(
          args[0].pkgVulnId
        );
      if (fixture) {
        return resolve(fixture);
      }
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getImagePackages: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsImagePackages();
      return resolve(fixture);
    }
    return next();
  },
  getImageResources: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoClusterSecurityInsightsImageResources();
      return resolve(fixture);
    }
    return next();
  },
  getAttackPaths: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoOrganizationSecurityInsightsAttackPaths();
      return resolve(fixture);
    }
    return next();
  },
  getAttackPathDetails: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoOrganizationSecurityInsightsAttackPathDetails();
      return resolve(fixture);
    }
    return next();
  },
  getResourceDetails: async ({ args, next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoOrganizationSecurityInsightsResourceDetails(
        args[0]?.id
      );
      if (fixture) {
        return resolve(fixture);
      }
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getBestPracticeOverview: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture =
        await demoOrganizationSecurityInsightsBestPracticeOverview();
      return resolve(fixture);
    }
    return next();
  },
  getImageSecurityOverview: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture =
        await demoOrganizationSecurityInsightsImageSecurityOverview();
      return resolve(fixture);
    }
    return next();
  },
  getAttackPathsOverview: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture =
        await demoOrganizationSecurityInsightsAttackPathsOverview();
      return resolve(fixture);
    }
    return next();
  },

  getClustersSettings: async ({ next, resolve }) => {
    if (getIsClusterDemoMode()) {
      const fixture = await demoOrganizationSecurityInsightsClustersSettings();
      return resolve(fixture);
    }
    return next();
  },
  getSingleClusterSettings: async ({ args, next, resolve }) => {
    const clusterId = args[0]?.id;
    if (
      clusterId &&
      (getIsClusterDemoMode() || isSimulatedDemoClusterId(clusterId))
    ) {
      const fixture = await demoOrganizationSecurityInsightsClustersSettings();
      const clusterSettings =
        fixture.clusters?.find((entry) => entry.id === clusterId) ??
        fixture.clusters?.[0];

      if (!clusterSettings) {
        return resolve({ settings: undefined });
      }

      // Demo list fixtures only embed the classic demo cluster id; remap id for OP demo.
      const settings =
        clusterSettings.id === clusterId
          ? clusterSettings
          : { ...clusterSettings, id: clusterId };

      return resolve({ settings });
    }
    return next();
  },

  getWorkloads: async ({ next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture = await demoOrganizationSecurityInsightsWorkloads();
      return resolve(fixture);
    }
    return next();
  },

  getVulnerabilitiesTrend: async ({ args, next, resolve }) => {
    if (getIsSecurityDemoMode()) {
      const fixture =
        await demoOrganizationSecurityInsightsVulnerabilitiesTrend(
          args[0]?.startTime,
          args[0]?.endTime,
          args[0]?.stepSeconds
        );
      return resolve(fixture);
    }
    return next();
  },
};
